import { RangeHorizon, rangeToResampleMapping } from '@chart';
import { Button } from '@design/components/button/Button';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { SmartTable } from '@design/components/smart-table';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { uniqueBy } from '@helpers';
import { Filter } from '@terminal/components/filter/Filter';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { appPaths } from '@terminal/routes/app-paths';
import {
  getInsightEpisodeDates,
  getInsightTotalTradingDays,
} from '@terminal/services/insights/utils/insight-utils';
import { InsightPastEpisodesData } from '@terminal/views/alfred/quick-analysis/use-chat/assistant-response.type';
import {
  DEFAULT_EPISODES_TO_SHOW,
  EMPTY_FILTER_LABELS,
  useCollapsibleEpisodesTable,
} from '@terminal/widgets/past-episodes-widget/hooks/useCollapsibleEpisodesTable';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useMatch, useNavigate } from 'react-router';

import sharedStyles from '../../PastEpisodesWidget.module.scss';
import { PastEpisodesChart } from '../past-episodes-chart/PastEpisodesChart';
import styles from './InsightPastEpisodes.module.scss';
import {
  getColumns,
  getHorizonsLabels,
  mapEpisodesToTableRow,
} from './utils/utils';

export const PAST_EPISODES_HORIZONS = [
  RangeHorizon.OneMonth,
  RangeHorizon.ThreeMonths,
  RangeHorizon.SixMonths,
  RangeHorizon.OneYear,
  RangeHorizon.ThreeYears,
  RangeHorizon.Max,
];

export type EpisodeTableRow = {
  key: string;
  episodeNo: number;
  date: string;
  tradingDays: number;
};

export const InsightPastEpisodes = ({
  insight,
  entity,
}: InsightPastEpisodesData) => {
  const { t } = useTranslation(['widget', 'chart']);
  const {
    isCollapsed,
    tableRef,
    activeSortOrderOption,
    setActiveSortOrderOption,
    sortOrderFilter,
    handleViewMoreClick,
  } = useCollapsibleEpisodesTable();

  const navigate = useNavigate();
  const match = useMatch(appPaths.chart);

  const episodes = useMemo(
    () =>
      [...insight.episodes].sort((a, b) =>
        activeSortOrderOption.value.id === 'asc'
          ? a.start_date.localeCompare(b.start_date)
          : b.start_date.localeCompare(a.start_date)
      ),
    [insight.episodes, activeSortOrderOption.value.id]
  );

  const tableEpisodes = isCollapsed
    ? episodes.slice(0, DEFAULT_EPISODES_TO_SHOW)
    : episodes;

  const totalTradingDays = getInsightTotalTradingDays(insight);

  const [activeHorizonIdx, setActiveHorizonIdx] = useState(5);
  const handleChangeHorizon = (index: number) => setActiveHorizonIdx(index);

  const columns = getColumns(totalTradingDays, t);

  const conditions = useMemo(() => {
    const { entity, snake } = insight.metadata;
    const uniqueConditions = uniqueBy(insight.conditions, 'snake');

    const isInInsightsSnakes = uniqueConditions.some(
      condition =>
        condition.snake === snake ||
        (condition.snake.includes('price') && condition.snake === snake)
    );

    if (isInInsightsSnakes) {
      return insight.conditions;
    }

    return [
      {
        entity,
        snake,
      },
      ...uniqueConditions,
    ];
  }, [insight.conditions, insight.metadata]);

  const assets = conditions.map((condition, index) => ({
    tag: entity.tag,
    snake: condition.snake,
    paneIndex: index,
  }));

  const handleFormatAssetsParams = () => {
    return conditions.map(
      (condition, index) => `${condition.entity};${condition.snake};${index}`
    );
  };

  const handleSamePathReload = () => {
    if (match?.pathname === appPaths.chart) {
      navigate(0);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.titleLabel}>
        {t('widget:pastEpisode.whenHasThisHappened')}
      </div>
      <div
        className={styles.descriptionLabel}
        data-testid="episode-description"
      >
        <span>
          <span>{t('widget:pastEpisode:title.occurrences')}</span>
          <span className={sharedStyles.pastEpisodeKey} />
          <TooltipContainer
            label={t('analyze:experiment.chart.tooltip')}
            showUnderline
          >
            <span>
              {t('widget:pastEpisode:title.episodes', {
                episodes: getInsightEpisodeDates(episodes).length,
              })}
            </span>
          </TooltipContainer>
          {','}
          <span>
            {t('widget:pastEpisode:title.days', {
              totalTradingDays,
            })}
          </span>
        </span>
      </div>
      <div className={sharedStyles.pastEpisodesButtons}>
        <ButtonGroup
          className={sharedStyles.styledButtonGroup}
          data-active-index={activeHorizonIdx}
          labels={getHorizonsLabels(t, PAST_EPISODES_HORIZONS)}
          onClick={handleChangeHorizon}
          activeIndex={activeHorizonIdx}
        />
        <Tooltip label={t('chart:fullscreen')}>
          <Button
            className={sharedStyles.styledButton}
            iconName="Expand"
            variant="empty"
            size="small"
            onClick={() => {
              navigate({
                pathname: appPaths.chart,
                search: createSearchParams({
                  asset: handleFormatAssetsParams(),
                  horizon: PAST_EPISODES_HORIZONS[activeHorizonIdx],
                  primary: conditions[0].snake,
                  resample:
                    rangeToResampleMapping[
                      PAST_EPISODES_HORIZONS[activeHorizonIdx]
                    ],
                  'yAxisType.1': 'merged',
                }).toString(),
              });
              handleSamePathReload();
            }}
            data-testid="navigate-to-chart-page"
          />
        </Tooltip>
      </div>
      <PastEpisodesChart
        assets={assets}
        episodes={episodes}
        horizon={PAST_EPISODES_HORIZONS[activeHorizonIdx]}
        key={insight.id}
      />
      <div className={styles.filterWrapper}>
        <Filter
          filter={sortOrderFilter}
          labels={EMPTY_FILTER_LABELS}
          filterOptions={[activeSortOrderOption]}
          addFilterOption={({ option }) => setActiveSortOrderOption(option)}
          removeFilterOption={() => {}}
          onCustomRangeClick={() => {}}
          contentStyle={{
            minWidth: 'var(--radix-dropdown-menu-trigger-width)',
          }}
        />
      </div>
      <div
        className={clsx(styles.tableWrapper, {
          [styles.expanded]: !isCollapsed,
        })}
        ref={tableRef}
      >
        <SmartTable
          columns={columns}
          data={mapEpisodesToTableRow(tableEpisodes)}
          withBodyRowHover={false}
        />
      </div>
      {episodes.length > DEFAULT_EPISODES_TO_SHOW && (
        <div className={styles.viewMoreWrapper}>
          <ButtonLink
            className={sharedStyles.viewMoreButton}
            label={t(
              `widget:pastEpisode.table.${
                isCollapsed ? 'viewMore' : 'viewLess'
              }`
            )}
            onClick={handleViewMoreClick}
          />
        </div>
      )}
    </div>
  );
};
